import React from 'react';
import {
    Box, Container, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, useTheme, Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

type FeatureSupport = 'yes' | 'no' | 'partial' | 'soon';

interface ComparisonRow {
    feature: string;
    percoro: FeatureSupport;
    travelApps: FeatureSupport;
    socialMedia: FeatureSupport;
    category?: string;
}

const ComparisonSection: React.FC = () => {
    const theme = useTheme();

    const comparisons: ComparisonRow[] = [
        { feature: 'Import Bookings (PDF)', percoro: 'yes', travelApps: 'partial', socialMedia: 'no', category: 'Documentation' },
        { feature: 'Trip Itineraries', percoro: 'yes', travelApps: 'partial', socialMedia: 'no' },
        { feature: 'Trip Intelligence', percoro: 'yes', travelApps: 'yes', socialMedia: 'no' },
        { feature: 'Travel Photo Gallery', percoro: 'yes', travelApps: 'partial', socialMedia: 'yes', category: 'Sharing & Privacy' },
        { feature: 'Privacy & Visibility Controls', percoro: 'yes', travelApps: 'no', socialMedia: 'partial' },
        { feature: 'Share with Friends & Family', percoro: 'yes', travelApps: 'no', socialMedia: 'yes' },
        { feature: 'Trip Companions', percoro: 'yes', travelApps: 'no', socialMedia: 'no' },
        { feature: 'Share Trips Publicly', percoro: 'yes', travelApps: 'no', socialMedia: 'yes', category: 'Community' },
        { feature: 'Browse Real Traveler Trips', percoro: 'yes', travelApps: 'no', socialMedia: 'yes' },
        { feature: 'Travel Community', percoro: 'yes', travelApps: 'no', socialMedia: 'yes' },
        { feature: 'Free to Use', percoro: 'yes', travelApps: 'partial', socialMedia: 'yes', category: 'General' },
        { feature: 'Book Flights & Hotels', percoro: 'soon', travelApps: 'yes', socialMedia: 'no' },
        { feature: 'Group Trips', percoro: 'soon', travelApps: 'partial', socialMedia: 'no' },
    ];

    const renderIcon = (value: FeatureSupport) => {
        switch (value) {
            case 'yes': return <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: '1.8rem' }} />;
            case 'partial': return <RemoveCircleOutlineIcon sx={{ color: '#FFA726', fontSize: '1.8rem' }} />;
            case 'no': return <CancelIcon sx={{ color: '#ccc', fontSize: '1.8rem' }} />;
            case 'soon': return <Chip label="Soon" size="small" sx={{ bgcolor: '#f0faf9', color: '#2D6E6A', fontWeight: 700, fontSize: '0.7rem' }} />;
        }
    };

    return (
        <Box sx={{ py: { xs: 8, md: 12 }, background: theme.palette.background.default }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
                        How We Compare
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '700px', mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.6 }}>
                        Document, share, and discover trips — with privacy controls you won't find anywhere else.
                    </Typography>
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ background: theme.palette.background.paper }}>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'text.primary', py: 3, pl: 4, width: '40%', minWidth: { xs: 120, sm: 160 } }}>
                                    Feature
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: theme.palette.primary.main, py: 3, minWidth: { xs: 80, sm: 120 } }}>
                                    Percoro
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'text.primary', py: 3, minWidth: { xs: 80, sm: 120 } }}>
                                    Travel Apps
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'text.primary', py: 3, pr: 4, minWidth: { xs: 80, sm: 120 } }}>
                                    Social Media
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {comparisons.map((row, index) => (
                                <TableRow key={index}
                                    sx={{
                                        '&:nth-of-type(odd)': { background: 'rgba(0,0,0,0.02)' },
                                        '&:hover': { background: `${theme.palette.primary.main}08` },
                                    }}>
                                    <TableCell sx={{ py: 2.5, pl: 4, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: 'text.primary' }}>
                                                {row.feature}
                                            </Typography>
                                            {row.category && (
                                                <Chip label={row.category} size="small"
                                                    sx={{ bgcolor: '#f5f5f5', color: '#888', fontSize: '0.65rem', height: 18 }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        {renderIcon(row.percoro)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        {renderIcon(row.travelApps)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 2.5, pr: 4, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                        {renderIcon(row.socialMedia)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                        <Typography variant="body2" color="text.secondary">Available</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RemoveCircleOutlineIcon sx={{ color: '#FFA726', fontSize: '1.2rem' }} />
                        <Typography variant="body2" color="text.secondary">Limited</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelIcon sx={{ color: '#ccc', fontSize: '1.2rem' }} />
                        <Typography variant="body2" color="text.secondary">Not Available</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Soon" size="small" sx={{ bgcolor: '#f0faf9', color: '#2D6E6A', fontWeight: 700, fontSize: '0.7rem' }} />
                        <Typography variant="body2" color="text.secondary">Coming Soon</Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default ComparisonSection;